import type { Translation } from 'Store'
import getTranslation from 'Util/getTranslation'
import { TextField, type TextFieldProps } from '@mui/material'
import type { default as React } from 'react'

type TranslationInputProps = {
  translations: Translation[]
  field: keyof Translation
  label: string
  locale: string
  setTranslations: (updatedTranslations: Translation[]) => void
} & TextFieldProps

/**
 * Renders a TextField component for translation input and handles the change in translation values.
 */
function TranslationInput({
  translations,
  field,
  label,
  locale,
  setTranslations,
  ...textFieldProps
}: Readonly<TranslationInputProps>) {
  const handleTranslationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target

    // Find the index of the translation with the matching locale
    const foundTranslationIndex = translations.findIndex(
      translation => translation.locale === locale
    )

    if (foundTranslationIndex !== -1) {
      // Update the field of the found translation object with the new value
      const updatedTranslation = {
        ...translations[foundTranslationIndex],
        [field]: value
      }

      // Create a copy of the translations array and replace the found translation with the updated one
      const updatedTranslations = [...translations]
      updatedTranslations[foundTranslationIndex] = updatedTranslation

      // Set the updated translations in the state using setTranslations
      setTranslations(updatedTranslations)
      return
    }

    // If the translation with the matching locale is not found, create a new translation object
    const newTranslation = {
      locale: locale,
      [field]: value
    } as Translation
    // Set the updated translations in the state using setTranslations
    setTranslations([...translations, newTranslation])
  }

  const translation = getTranslation(translations, locale)[field]

  return (
    <TextField
      required
      label={label}
      name={`${locale}.${field}`}
      value={translation !== '-' ? translation : ''}
      onChange={handleTranslationChange}
      {...textFieldProps}
    />
  )
}

export default TranslationInput
